<script lang="ts" setup>
  import { CloseIcon, DragVerticalIcon } from '@/config/icons.ts'
  import { type Component, onErrorCaptured, ref, shallowRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useDashboard } from '@/composables/useDashboard.ts'

  interface Props {
    component: Component | string | null
    properties?: Record<string, unknown>,
  }

  export type ProxyContextMenuPayload = {
    event: MouseEvent
  }

  const props = defineProps<Props>()
  const emit = defineEmits<{
    (e: 'remove', payload: { root: HTMLDivElement; id: string }): void
  }>()

  const root = ref<HTMLDivElement>()
  const widgetError = shallowRef<unknown>()

  const isEditing = useDashboard().isEditing
  const isSaving = useDashboard().isSaving
  const { t } = useI18n()
  const getWidgetTitle = () => t(String(props.properties?.id ?? ''))

  const bus = useEventBus<ProxyContextMenuPayload>(
    `dashboard:${props.properties?.id!}:contextmenu`
  )

  onErrorCaptured((error, _instance, info) => {
    widgetError.value = error
    console.error('Dashboard widget failed', {
      id: props.properties?.id,
      error,
      info
    })
    return false
  })

  const handleRemove = () => {
    if (root.value) {
      emit('remove', { root: root.value, id: props.properties!.id! as string })
    }
  }

  const onContextMenu = (event: MouseEvent): void => {
    if (!isEditing.value || isSaving.value) return

    event.preventDefault()

    bus.emit({ event })
  }
</script>

<template>
  <div ref="root"
       class="grid-stack-item group @container h-full w-full min-w-0"
       @contextmenu="onContextMenu">
    <div
      v-if="isEditing && !isSaving"
      class="absolute right-1 top-1 z-50 flex items-center gap-1 rounded border border-gray-200 bg-white p-0.5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <DragVerticalIcon
        class="dashboard-widget-drag-handle size-6 cursor-move rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-100"
        :title="t('move')" />
      <button
        type="button"
        class="flex size-6 items-center justify-center rounded text-gray-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950 dark:hover:text-red-300"
        :title="t('remove')"
        :aria-label="t('remove')"
        @click.stop="handleRemove">
        <CloseIcon class="size-4" />
      </button>
    </div>
    <div
      class="grid-stack-item-content titrop-widget-gridstack scroll-content @container min-w-0"
      :class="{ 'flex flex-col !overflow-hidden': properties?.id === 'todayhours' }">
      <span class="shrink-0 overflow-ellipsis break-words font-semibold l w-full flex justify-center mb-2 mt-2" v-if="properties?.widgetTitle">
          {{ getWidgetTitle() }}
        </span>
      <component
        :is="component"
        v-if="component && !widgetError"
        v-bind="{ ...properties, ...$attrs }"
        :class="{ 'min-h-0 flex-1': properties?.id === 'todayhours' }" />
      <Message v-else-if="widgetError" size="small" severity="error">
        <template #icon>
          <component is="ExclamationTriangleIcon" />
        </template>
        <p>{{ t('loadError') }}</p>
      </Message>
      <Message v-else size="small" severity="warn">
        <template #icon>
          <component is="InfoIcon" />
        </template>
        <p>{{ t('unavailable') }}</p>
      </Message>
    </div>
  </div>
</template>

<style scoped>
  :deep(.scroll-content::-webkit-scrollbar) {
    width: 8px;
  }

  :deep(.scroll-content::-webkit-scrollbar-track) {
    background: transparent;
    border-radius: 8px;
  }

  :deep(.scroll-content::-webkit-scrollbar-thumb) {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 8px;
  }

  /* Firefox */
  :deep(.scroll-content) {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
  }
</style>

<i18n>
{
  "de": {
    "move": "Widget verschieben",
    "remove": "Widget entfernen",
    "loadError": "Dieses Widget konnte nicht geladen werden",
    "unavailable": "Dieses Widget ist nicht mehr verfügbar",
    "infoboard": "Infoboard",
    "worktime": "Arbeitszeiterfassung",
    "statechange": "Mein Status",
    "editworktime": "Arbeitszeitkorrekturen",
    "timetrackings": "Aktive Time-Trackings",
    "todayhours": "Heutige Zeit",
    "servicerevenue": "Dienstleistungsumsatz",
    "yearrevenue": "Jahresumsatz"
  },
  "en": {
    "move": "Move widget",
    "remove": "Remove widget",
    "loadError": "This widget could not be loaded",
    "unavailable": "This widget is no longer available",
    "infoboard": "Infoboard",
    "worktime": "Work time tracking",
    "statechange": "My status",
    "editworktime": "Work time corrections",
    "timetrackings": "Active time trackings",
    "todayhours": "Today's time",
    "servicerevenue": "Service revenue",
    "yearrevenue": "Annual revenue"
  }
}
</i18n>
